import {
  ASSET_METADATA_KEY,
  SEARCH_ASSET_METADATA_PANEL,
  UNSAVED_CHANGES_MESSAGE,
} from '../../constants/data';
import type { AppDispatch, GetState } from '../../store/types';
import { getObjectLink } from '../../utils/get_object_link';
import { historyPush } from '../history/actions';
import { resetPanelsPristine } from '../panels/pristine/slice';
import type { PanelsPristine } from '../panels/pristine/types';
import { selectIsSharePage } from '../share/selectors';
import { selectNavigationKey, selectSearchItemByOffset } from './selectors';
import {
  popNavigationHistoryItem,
  pushNavigationHistoryItem,
  updateLastNavigationHistoryItem,
  updateNthNavigationHistoryItem,
} from './slice';
import type { NavigationHistoryItem } from './types';

const shouldNavigateToNext = (hasUnsavedData: boolean) => {
  const proceed = hasUnsavedData
    ? window.confirm(UNSAVED_CHANGES_MESSAGE)
    : true;
  return proceed;
};

const prioritizedHistoryPush = (url: string) => (dispatch: AppDispatch) =>
  // setTimeout required to prevent <Prompt /> from showing in panel
  // since we're running a manual one here ( shouldNavigateToNext ).
  setTimeout(() => {
    dispatch(historyPush(url));
  });

const isPanelsDirty = (panelsPristine: PanelsPristine) =>
  !panelsPristine[ASSET_METADATA_KEY || SEARCH_ASSET_METADATA_PANEL];

export const pushUpdateNavigationHistory =
  (
    navigationHistoryItem: Omit<
      NavigationHistoryItem,
      'navigationKey' | 'url' | 'selectedIds' | 'itemId'
    >,
  ) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    const navigationKey = selectNavigationKey(state);
    const lastNavigationHistoryItem = state.navigationHistory.history.at(-1);
    const id = state.routeParams.params.id;

    if (lastNavigationHistoryItem?.navigationKey === navigationKey) {
      dispatch(
        updateLastNavigationHistoryItem({
          listIds: navigationHistoryItem.listIds,
          url: state.router.location.pathname + state.router.location.search,
        }),
      );
    } else {
      dispatch(
        pushNavigationHistoryItem({
          ...navigationHistoryItem,
          selectedIds: [],
          navigationKey,
          url: state.router.location.pathname + state.router.location.search,
          itemId: id,
        }),
      );
    }
  };

export const upToPrevNavigationItem =
  () => (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    const navigationKey = selectNavigationKey(state);
    const lastNavigationItem = state.navigationHistory.history.at(-1);
    const prevNavigationItem = state.navigationHistory.history.at(-2);
    const hasUnsavedData = isPanelsDirty(state.panelsPristine);

    const proceed = shouldNavigateToNext(hasUnsavedData);

    if (!proceed) {
      return;
    }

    if (hasUnsavedData && proceed) {
      dispatch(resetPanelsPristine());
    }

    if (
      lastNavigationItem &&
      lastNavigationItem.navigationKey !== navigationKey
    ) {
      dispatch(prioritizedHistoryPush(lastNavigationItem.url));
      return;
    }

    if (
      lastNavigationItem?.navigationKey === navigationKey &&
      prevNavigationItem
    ) {
      dispatch(popNavigationHistoryItem());
      dispatch(prioritizedHistoryPush(prevNavigationItem.url));
    }
  };

export const navigationHistoryGoPrev =
  (currentId: string | undefined) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    const isSharePage = selectIsSharePage(state);
    const searchItem = selectSearchItemByOffset(state, currentId, -1);
    const hasUnsavedData = isPanelsDirty(state.panelsPristine);

    const proceed = shouldNavigateToNext(hasUnsavedData);

    if (!proceed || !searchItem) {
      return;
    }

    if (hasUnsavedData && proceed) {
      dispatch(resetPanelsPristine());
    }

    dispatch(
      prioritizedHistoryPush(
        getObjectLink(isSharePage, searchItem.id, searchItem.object_type),
      ),
    );
    dispatch(
      updateNthNavigationHistoryItem({
        index: -2,
        historyItem: {
          selectedIds: [searchItem.id],
        },
      }),
    );
  };

export const navigationHistoryGoNext =
  (currentId: string | undefined) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    const isSharePage = selectIsSharePage(state);
    const searchItem = selectSearchItemByOffset(state, currentId, 1);
    const hasUnsavedData = isPanelsDirty(state.panelsPristine);

    const proceed = shouldNavigateToNext(hasUnsavedData);

    if (!proceed || !searchItem) {
      return;
    }

    if (hasUnsavedData && proceed) {
      dispatch(resetPanelsPristine());
    }

    dispatch(
      prioritizedHistoryPush(
        getObjectLink(isSharePage, searchItem.id, searchItem.object_type),
      ),
    );
    dispatch(
      updateNthNavigationHistoryItem({
        index: -2,
        historyItem: {
          selectedIds: [searchItem.id],
        },
      }),
    );
  };
